import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ordersAPI } from '../../utils/api';
import { LoadingSpinner, StatusBadge } from '../../components/common';

const STEPS = [
  { key: 'placed',    label: 'Order Placed',  icon: '📋' },
  { key: 'inprogress',label: 'Inprogress',     icon: '📦' },
  { key: 'shipped',   label: 'Shipped',        icon: '🚚' },
  { key: 'delivered', label: 'Delivered',      icon: '✅' },
];

const fmt = n => '$' + parseFloat(n || 0).toFixed(2);

const TrackOrderPage = () => {
  const { orderNumber: paramOrder } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(paramOrder || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setOrder(null);
    setSearched(true);
    try {
      const res = await ordersAPI.getById(q);
      setOrder(res.data);
      navigate(`/track-order/${q}`, { replace: true });
    } catch {
      setError('No order found with that number. Please double-check and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if URL has an order number
  React.useEffect(() => {
    if (!paramOrder || searched) return;
    setInput(paramOrder);
    setLoading(true);
    setError('');
    setOrder(null);
    setSearched(true);
    ordersAPI.getById(paramOrder)
      .then(res => setOrder(res.data))
      .catch(() => setError('No order found with that number. Please double-check and try again.'))
      .finally(() => setLoading(false));
  }, [paramOrder, searched]);

  const stepIndex = order ? STEPS.findIndex(s => s.key === order.status) : -1;
  const addr = order
    ? (typeof order.shipping_address === 'string' ? JSON.parse(order.shipping_address) : order.shipping_address)
    : null;

  return (
    <div className="container" style={{ padding: '2rem 1rem', maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🚚</div>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1f2937', marginBottom: '0.4rem' }}>Track Your Order</h1>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Enter your order number to get real-time status updates</p>
      </div>

      {/* Search Box */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', maxWidth: 520, margin: '0 auto 2rem' }}>
        <input
          className="form-input"
          style={{ flex: 1, fontSize: '1rem', letterSpacing: '0.03em' }}
          placeholder="e.g. ORD12345678"
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase())}
          autoFocus
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !input.trim()}
          style={{ whiteSpace: 'nowrap', minWidth: 110 }}
        >
          {loading ? 'Searching…' : 'Track Order'}
        </button>
      </form>

      {/* Loading */}
      {loading && <LoadingSpinner />}

      {/* Error */}
      {error && !loading && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '1rem 1.25rem', color: '#dc2626', textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
          ❌ {error}
        </div>
      )}

      {/* Result */}
      {order && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Order header */}
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Order Number</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1B4F8A', letterSpacing: '0.04em' }}>#{order.order_number}</div>
            </div>
            <StatusBadge status={order.status} />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 3 }}>Placed on</div>
              <div style={{ fontWeight: 600, color: '#374151' }}>
                {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Progress Tracker */}
          {order.status !== 'cancelled' && (
            <div className="card">
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: '#1f2937' }}>Shipment Progress</h3>
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
                {/* Track line background */}
                <div style={{ position: 'absolute', top: 20, left: '10%', right: '10%', height: 4, background: '#e5e7eb', borderRadius: 2, zIndex: 0 }} />
                {/* Track line filled */}
                <div style={{
                  position: 'absolute', top: 20, left: '10%',
                  width: stepIndex >= 0 ? `${(stepIndex / (STEPS.length - 1)) * 80}%` : '0%',
                  height: 4, background: '#2563eb', borderRadius: 2, zIndex: 1, transition: 'width 0.6s ease',
                }} />
                {STEPS.map((step, i) => (
                  <div key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, width: '25%' }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: '50%',
                      background: i <= stepIndex ? '#2563eb' : '#f3f4f6',
                      border: i === stepIndex ? '3px solid #1d4ed8' : '3px solid ' + (i < stepIndex ? '#2563eb' : '#e5e7eb'),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: i <= stepIndex ? '1.2rem' : '1rem',
                      transition: 'all 0.3s',
                      boxShadow: i === stepIndex ? '0 0 0 4px rgba(37,99,235,0.15)' : 'none',
                    }}>
                      {i < stepIndex ? '✓' : step.icon}
                    </div>
                    <div style={{
                      fontSize: '0.72rem', marginTop: '0.5rem', fontWeight: i <= stepIndex ? 700 : 400,
                      color: i <= stepIndex ? '#1d4ed8' : '#9ca3af', textAlign: 'center', lineHeight: 1.3,
                    }}>
                      {step.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {order.status === 'cancelled' && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '1rem 1.25rem', color: '#dc2626', fontWeight: 600, textAlign: 'center' }}>
              ⚠️ This order has been cancelled.
            </div>
          )}

          {/* Items Table */}
          <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: '1rem', color: '#1f2937' }}>Items Ordered</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={th}>Product</th>
                    <th style={{ ...th, textAlign: 'center' }}>Qty</th>
                    <th style={{ ...th, textAlign: 'right' }}>Unit Price</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ ...td }}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>{item.name}</div>
                        {item.sku && <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>SKU: {item.sku}</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'center', color: '#6b7280' }}>{item.qty}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#374151' }}>{fmt(item.unit_price)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#1f2937' }}>{fmt(item.total_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary + Address */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

            {/* Order Summary */}
            <div className="card">
              <h3 style={{ fontWeight: 700, marginBottom: '1rem', color: '#1f2937' }}>Order Summary</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                <SummaryRow label="Subtotal" value={fmt(order.subtotal)} />
                <SummaryRow label="Shipping" value={fmt(order.shipping_fee)} />
                {parseFloat(order.discount || 0) > 0 && (
                  <SummaryRow label="Shipping Discount" value={`-${fmt(order.discount)}`} color="#16a34a" />
                )}
                <SummaryRow label="Tax" value={fmt(order.tax)} />
                <div style={{ borderTop: '2px solid #e5e7eb', marginTop: '0.25rem', paddingTop: '0.5rem' }}>
                  <SummaryRow label="Total" value={fmt(order.total)} bold />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {addr && (
              <div className="card">
                <h3 style={{ fontWeight: 700, marginBottom: '1rem', color: '#1f2937' }}>Shipping To</h3>
                <div style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 600 }}>{addr.full_name}</div>
                  {addr.phone && <div style={{ color: '#6b7280' }}>{addr.phone}</div>}
                  <div>{addr.address_line1}</div>
                  {addr.address_line2 && <div>{addr.address_line2}</div>}
                  <div>{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</div>
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

const th = { padding: '0.6rem 0.75rem', fontWeight: 700, color: '#374151', textAlign: 'left', borderBottom: '2px solid #e5e7eb' };
const td = { padding: '0.65rem 0.75rem', verticalAlign: 'top' };

const SummaryRow = ({ label, value, bold, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ fontWeight: bold ? 800 : 500, color: color || '#1f2937' }}>{value}</span>
  </div>
);

export default TrackOrderPage;
